/* ==========================================================================
   BYAYA — the gold / silver switch on the collection page
   --------------------------------------------------------------------------
   The switch sits in the bar above the grid (snippets/byaya-finish-swatch.liquid,
   rendered by sections/main-collection.liquid, styled in byaya-finish-swatch.css):
   one toggle, a small square track with both metals in it. One tap turns
   every card on the page gold or silver, and the vivid chip slides across
   and changes metal with them.

   What a tap does, per card
   - sprite card (snippets/byaya-sprite-card.liquid): the picture keeps
     moving on the shared clock, only the file behind it changes from the
     gold sprite to the silver one. The cards in view are decoded first and
     swapped together, so the grid changes in one go instead of card by
     card; cards further down simply get the other file and load it when
     they scroll in, as they would have anyway. The hover picture (the
     theme shows the next product picture on hover) becomes the first
     picture after the silver animation, so a silver card does not flash a
     gold model photo.
   - plain card (no sprite): the silver variant's own picture is made the
     active one, the theme's way (the --active / --show-on-hover classes it
     uses itself), so its arrows, swipe and dots keep working.
   - the change reads as one motion: while the other finish is fetched,
     the pictures of the cards in view dip a little (class byaya-swapping,
     styled in the stylesheet), and they come back up with the new finish
     in place — never a switch that says silver above a grid that is still
     gold, and no jump when the files arrive. On a fast connection the dip
     is held for a moment so it is a movement and not a flicker.
   - links: the card's links get ?variant=<first silver variant> (the id
     comes from data-byaya-silver-variant on <product-block>, written by
     snippets/product-block.liquid), so the product page opens with silver
     already chosen. Back on gold the links are as the theme wrote them.
   - a product without a Finish option (nothing in data-byaya-*) is left alone.
   - the two small discs on a card (snippets/byaya-card-finish.liquid) turn
     that one card the same way, without touching the switch or the other
     cards; the switch afterwards turns everything again.

   The choice follows the customer (Aron, 29 sep: "doe beide"). It is kept
   for the visit (sessionStorage, this tab) and applied on every page —
   not only the next collection page, but also the cards in the menu, the
   cart drawer's cross-sell, "You may also like" under a product, search
   results and the home page: every card of a product that comes in both
   metals shows the chosen one and links to it. The switch itself is only
   in the collection bar; the other places have no room for one and need
   none. layout/theme.liquid reads the flag in the head and marks
   <html data-byaya-finish="silver">, and the stylesheet keeps a gold
   sprite invisible until it has been swapped — so a remembered silver
   page never shows gold first.

   The product page takes part in two ways: choosing a Finish disc there
   (Gold / Silver) is the same as using the switch, so the cards under the
   product and in the cart follow at once and the choice is kept; and a
   product opened in a silver visit without a variant in its address gets
   its Silver disc chosen for the customer (an address that names a
   variant is left as it is — someone sent that link on purpose).

   The bar and the grid are replaced by the theme when a filter or the sort
   changes (AJAX), the cart drawer redraws on every cart change, and the
   recommendations under a product arrive later; a watcher applies the
   current finish to whatever new cards and switch arrive, so nothing has
   to be wired up again.

   Loading: nothing extra on page load. When the pointer comes over the
   switch or the Finish discs, the other finish is fetched for the cards
   in view, so the tap is instant; on a saved-data or 2G/3G connection
   only on the tap itself.
   ========================================================================== */
(function () {
  'use strict';
  if (window.byayaFinishSwitch) return;
  window.byayaFinishSwitch = true;

  var KEY = 'byaya-finish';
  var SWITCH = '[data-byaya-finish-switch]';
  var DISC = '.option-selector[data-option="Finish"] input.opt-btn';   /* the Finish discs on a product page */
  var SWAPPING = 'byaya-swapping';                     /* on <product-block> while its pictures dip */
  var BLIND = 'byaya-blind';                           /* with it: the picture stays blank until the right file is in */
  var DIP_MS = 180;                                     /* the dip is at least this long, so it reads as a motion */
  var WAIT_MS = 2500;                                   /* longest wait for the other finish to decode */
  var DASH_OUT = 'byaya-dash-out';                     /* a picture dash outside the window of four */
  var DASHES = 4;                                       /* dashes in view at most: the long one and three short */
  var ATTRS = ['src', 'srcset', 'data-manual-src', 'data-manual-srcset'];
  var NAMES = { gold: 'Gold', silver: 'Silver' };
  var conn = navigator.connection || {};
  var slow = !!(conn.saveData || /(^|[^0-9])[23]g$/.test(conn.effectiveType || ''));
  var root = document.documentElement;
  var state = 'gold';
  try { if (sessionStorage.getItem(KEY) === 'silver') state = 'silver'; } catch (e) { /* private mode: gold */ }

  function remember(finish) {
    try {
      if (finish === 'gold') sessionStorage.removeItem(KEY);
      else sessionStorage.setItem(KEY, finish);
    } catch (e) { /* nothing to remember in, fine */ }
  }

  /* ---- one card ------------------------------------------------------- */

  function spriteOf(pb) { return pb.querySelector('.byaya-sprite[data-gold][data-silver]'); }

  /* the attribute values the sprite's <img> needs for the other finish:
     everything after the file name stays as it is */
  function spriteSwap(sp, finish) {
    var img = sp.querySelector('img');
    var from = sp.getAttribute('data-' + (sp.getAttribute('data-finish') || 'gold'));
    var to = sp.getAttribute('data-' + finish);
    if (!img || !from || !to) return null;
    var next = {};
    ATTRS.forEach(function (a) {
      var v = img.getAttribute(a);
      if (v) next[a] = v.split(from).join(to);
    });
    return { sp: sp, img: img, next: next, to: to, same: from === to };
  }

  function probeFor(swap) {
    var probe = new Image();
    var sizes = swap.img.getAttribute('sizes');
    if (sizes) probe.sizes = sizes;
    if (swap.next.srcset || swap.next['data-manual-srcset']) probe.srcset = swap.next.srcset || swap.next['data-manual-srcset'];
    probe.src = swap.next.src || swap.next['data-manual-src'] || (swap.to + '600');
    return probe;
  }

  function decoded(probe, ms) {
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () { if (!done) { done = true; resolve(); } };
      setTimeout(finish, ms);
      if (probe.decode) probe.decode().then(finish, finish);
      else { probe.onload = finish; probe.onerror = finish; }
    });
  }

  function inView(el) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || root.clientHeight;
    return r.bottom > -h * 0.5 && r.top < h * 1.5;
  }

  /* the picture dashes of a card (snippets/product-block.liquid draws one
     per picture): the dash of the picture shown is the long one, and no
     more than four are in view — the long one and three short (Aron,
     29 sep: "max 4, 3 kleine en 1 grote"). Those four are a window that
     slides along only when the picture shown leaves it, the way a
     phone's own photo strip moves; the window's place is read from the
     dashes themselves, so this works on any card at any moment. */
  function markDash(pb, active) {
    var dots = Array.prototype.slice.call(pb.querySelectorAll('.product-block__image-dot'));
    var n = dots.length;
    if (!n) return;
    var start = 0;
    for (var i = 0; i < n; i += 1) { if (!dots[i].classList.contains(DASH_OUT)) { start = i; break; } }
    if (active < start) start = active;
    else if (active > start + DASHES - 1) start = active - DASHES + 1;
    start = Math.max(0, Math.min(start, n - DASHES));
    dots.forEach(function (d, i) {
      d.classList.toggle('product-block__image-dot--active', i === active);
      d.classList.toggle(DASH_OUT, i < start || i > start + DASHES - 1);
    });
  }

  /* the theme's own way of showing a picture on a card: one --active, the
     next one --show-on-hover, the dashes along. keepActive: only move the hover
     picture (sprite cards keep their sprite in front). */
  function showMedia(pb, mediaId, keepActive) {
    var imgs = Array.prototype.slice.call(pb.querySelectorAll('.product-block__image'));
    if (!imgs.length) return;
    var idx = -1;
    imgs.forEach(function (el, i) { if (el.getAttribute('data-media-id') === String(mediaId)) idx = i; });
    if (idx < 0) return;
    var active, hover;
    if (keepActive) {
      active = imgs.filter(function (el) { return el.classList.contains('product-block__image--active'); })[0] || imgs[0];
      hover = imgs[idx + 1] || imgs[idx];
      if (hover === active) hover = imgs[idx];
    } else {
      active = imgs[idx];
      hover = imgs[(idx + 1) % imgs.length];
    }
    imgs.forEach(function (el) {
      el.classList.toggle('product-block__image--active', el === active);
      el.classList.toggle('product-block__image--show-on-hover', el === hover && el !== active);
    });
    active.classList.remove('product-block__image--inactivated');
    hover.classList.remove('product-block__image--inactivated');
    markDash(pb, imgs.indexOf(active));
  }

  /* the card's links: the chosen finish preselected, or the plain address */
  function setLinks(pb, variantId, backToStart) {
    pb.querySelectorAll('a.product-link, a.quickbuy-toggle').forEach(function (a) {
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      var url;
      try { url = new URL(href, window.location.href); } catch (e) { return; }
      if (backToStart || !variantId) url.searchParams.delete('variant');
      else url.searchParams.set('variant', variantId);
      a.setAttribute('href', url.pathname + url.search + url.hash);
    });
  }

  /* sets the card's state and links at once, and returns the picture
     change still to be painted (a sprite file swap and/or the theme's
     media switch), or null when nothing on the card changes */
  function applyCard(pb, finish) {
    var variant = pb.getAttribute('data-byaya-' + finish + '-variant');
    var media = pb.getAttribute('data-byaya-' + finish + '-media');
    if (!variant && !media) return null;                 /* no such finish here */
    if (pb.getAttribute('data-byaya-finish') === finish) return null;
    var sp = spriteOf(pb);
    if (!pb.hasAttribute('data-byaya-initial')) {
      /* first time we see this card: note the finish it was rendered in;
         if that is the one wanted, there is nothing to change */
      var initial = (sp && sp.getAttribute('data-finish')) || 'gold';
      pb.setAttribute('data-byaya-initial', initial);
      if (finish === initial) { pb.setAttribute('data-byaya-finish', finish); return null; }
    }
    pb.setAttribute('data-byaya-finish', finish);
    var swap = null;
    if (sp) {
      swap = spriteSwap(sp, finish);
      if (swap && swap.same) { sp.setAttribute('data-finish', finish); swap = null; }
    }
    setLinks(pb, variant, finish === pb.getAttribute('data-byaya-initial'));
    if (!swap && !media) return null;
    return { pb: pb, swap: swap, media: media, keepActive: !!sp };
  }

  /* the picture change itself */
  function paint(job, finish) {
    if (job.swap) {
      ATTRS.forEach(function (a) { if (job.swap.next[a]) job.swap.img.setAttribute(a, job.swap.next[a]); });
      job.swap.sp.setAttribute('data-finish', finish);
    }
    if (job.media) showMedia(job.pb, job.media, job.keepActive);
  }

  /* ---- the whole page --------------------------------------------------- */

  var run = 0;                                          /* numbers the runs, for the stamps */

  /* the picture change for cards in view, as one motion: the pictures
     dip, the other finish is decoded, then all of them are painted in the
     same frame and come back up together */
  function settle(jobs, finish, blind) {
    var stamp = String(++run);
    jobs.forEach(function (job) {
      job.pb.classList.add(SWAPPING);
      /* blind: the card came from the server in the other finish (page
         load, a filter, new cards) and must not show it first — its picture
         stays blank until the right file is in. A tap on the switch or on a
         card's disc is not blind: the old picture dips and the new one
         takes its place */
      if (blind) job.pb.classList.add(BLIND);
      job.pb.setAttribute('data-byaya-swapping', stamp);  /* whose dip this is */
    });
    var since = Date.now();
    var decodes = jobs.filter(function (job) { return job.swap; })
      .map(function (job) { return decoded(probeFor(job.swap), WAIT_MS); });
    Promise.all(decodes).then(function () {
      setTimeout(function () {
        /* a newer run (a second tap, or new cards arriving) re-stamps the
           cards it changes and paints those itself; whatever still carries
           this run's stamp is painted here — also when a newer run with the
           same finish came by, because that one skips cards already marked */
        var mine = jobs.filter(function (job) { return job.pb.getAttribute('data-byaya-swapping') === stamp; });
        mine.forEach(function (job) { paint(job, finish); });
        setTimeout(function () {
          mine.forEach(function (job) {
            if (job.pb.getAttribute('data-byaya-swapping') !== stamp) return;
            job.pb.classList.remove(SWAPPING);
            job.pb.classList.remove(BLIND);
            job.pb.removeAttribute('data-byaya-swapping');
          });
        }, 30);
      }, Math.max(0, DIP_MS - (Date.now() - since)));
    });
  }

  /* blind: see settle() — true for cards the server rendered, false for a tap */
  function applyAll(blind) {
    var finish = state;
    root.setAttribute('data-byaya-finish', finish);

    document.querySelectorAll(SWITCH).forEach(function (sw) {
      sw.setAttribute('data-finish', finish);
      sw.setAttribute('aria-checked', finish === 'silver' ? 'true' : 'false');
      sw.setAttribute('aria-label', finish === 'silver' ? 'Show in gold' : 'Show in silver');
      var name = sw.querySelector('[data-byaya-finish-name]');
      if (name) name.textContent = NAMES[finish] || finish;
    });

    var now = [], later = [];
    document.querySelectorAll('product-block').forEach(function (pb) {
      var job = applyCard(pb, finish);
      if (!job) return;
      (pb.offsetParent && inView(pb) ? now : later).push(job);
    });

    /* out of view (or hidden, like the cards in the menu): painted at
       once, a new sprite file loads when the card comes into view */
    later.forEach(function (job) { paint(job, finish); });
    if (now.length) settle(now, finish, blind);
  }

  /* one card, from its own discs: this card only, the switch and the
     other cards stay as they are */
  function applyOne(pb, finish) {
    var job = applyCard(pb, finish);
    if (!job) return;
    if (pb.offsetParent && inView(pb)) settle([job], finish, false);
    else paint(job, finish);
  }

  function choose(finish) {
    if (!NAMES[finish] || finish === state) return;
    state = finish;
    remember(finish);
    applyAll(false);
  }

  /* warm up the other finish: for one card, or for all cards in view */
  function warmCard(pb, other) {
    if (pb.getAttribute('data-byaya-warm') === other) return;
    var sp = spriteOf(pb);
    if (!sp) return;
    var swap = spriteSwap(sp, other);
    if (!swap || swap.same) return;
    pb.setAttribute('data-byaya-warm', other);
    probeFor(swap);
  }
  function warm() {
    if (slow) return;
    var other = state === 'silver' ? 'gold' : 'silver';
    document.querySelectorAll('product-block').forEach(function (pb) {
      if (pb.offsetParent && inView(pb)) warmCard(pb, other);
    });
  }

  document.addEventListener('click', function (evt) {
    var sw = evt.target.closest ? evt.target.closest(SWITCH) : null;
    if (!sw) return;
    evt.preventDefault();
    choose(state === 'silver' ? 'gold' : 'silver');
  });
  /* the discs on a card: caught before the theme sees the click, so the
     card's link and quick-buy stay out of it */
  document.addEventListener('click', function (evt) {
    var pick = evt.target.closest ? evt.target.closest('[data-byaya-card-finish-pick]') : null;
    if (!pick) return;
    evt.preventDefault();
    evt.stopPropagation();
    var pb = pick.closest('product-block');
    var finish = pick.getAttribute('data-byaya-card-finish-pick');
    if (pb && NAMES[finish]) applyOne(pb, finish);
  }, true);
  document.addEventListener('mouseover', function (evt) {
    var pick = evt.target.closest ? evt.target.closest('[data-byaya-card-finish]') : null;
    var pb = pick && pick.closest('product-block');
    if (pb && !slow) warmCard(pb, (pb.getAttribute('data-byaya-finish') || 'gold') === 'silver' ? 'gold' : 'silver');
  }, { passive: true });
  /* a Finish disc on a product page (or in a quick-buy) counts as the switch */
  document.addEventListener('change', function (evt) {
    var t = evt.target;
    if (!t || !t.matches || !t.matches(DISC) || !t.checked) return;
    var finish = String(t.value || '').toLowerCase();
    if (NAMES[finish]) choose(finish);
  });
  document.addEventListener('mouseover', function (evt) {
    if (evt.target.closest && (evt.target.closest(SWITCH) || evt.target.closest('.option-selector[data-option="Finish"]'))) warm();
  }, { passive: true });
  document.addEventListener('focusin', function (evt) {
    if (evt.target.closest && (evt.target.closest(SWITCH) || evt.target.closest('.option-selector[data-option="Finish"]'))) warm();
  });

  /* a product opened in a silver visit, without a variant in the address:
     its Silver disc is chosen for the customer, the theme's own way (a
     click, so the theme changes variant, picture and price as usual) */
  function preselect() {
    if (state !== 'silver') return;
    var params;
    try { params = new URL(window.location.href).searchParams; } catch (e) { return; }
    if (params.has('variant')) return;
    var form = document.querySelector('.js-product');
    if (!form) return;
    var discs = form.querySelectorAll(DISC);
    if (!discs.length) return;
    var checked = Array.prototype.filter.call(discs, function (d) { return d.checked; })[0];
    if (checked && String(checked.value).toLowerCase() === 'silver') return;
    var silver = Array.prototype.filter.call(discs, function (d) { return String(d.value).toLowerCase() === 'silver' && !d.disabled; })[0];
    if (!silver) return;
    /* the theme loads its variant picker lazily and marks it "loaded";
       a click before that only ticks the radio and the gallery does not
       follow — so wait for it, the way the theme's own gallery does */
    whenLoaded(form.querySelector('variant-picker'), function () { silver.click(); });
  }

  function whenLoaded(el, cb) {
    if (!el || el.hasAttribute('loaded')) { cb(); return; }
    if (window.theme && typeof window.theme.whenComponentLoaded === 'function') { window.theme.whenComponentLoaded(el, cb); return; }
    var t0 = Date.now();
    (function poll() {
      if (el.hasAttribute('loaded') || Date.now() - t0 > 5000) cb();
      else setTimeout(poll, 50);
    })();
  }

  /* ---- the picture dashes ----------------------------------------------- */

  /* The theme marks the dash whose number is the picture number it was
     asked for. A swipe past the last picture (or back past the first) asks
     for picture 10, or -1: the picture itself wraps round to the first
     (getImageAt), but no dash has that number, so nothing is marked until
     the next swipe (Aron, 29 sep: "niks meer geselecteerd" — the first
     cause was the theme's two dots plus a "more" dot, now one dash per
     picture in snippets/product-block.liquid; this is the last gap). Put
     right once, on the card element itself: after the theme's own
     setActiveImage the dashes are marked from the picture actually shown
     (markDash, which also keeps the window of four). The theme's script
     comes after this one, so wait until it has defined the element. */
  function dashesFollow() {
    var C = window.customElements && customElements.get('product-block');
    if (!C || !C.prototype || typeof C.prototype.setActiveImage !== 'function' || C.prototype.byayaDashes) return;
    var theirs = C.prototype.setActiveImage;
    C.prototype.setActiveImage = function (index) {
      theirs.call(this, index);
      var n = this.images ? this.images.length : this.querySelectorAll('.product-block__image').length;
      if (!n) return;
      markDash(this, ((index % n) + n) % n);
    };
    C.prototype.byayaDashes = true;
  }

  /* the theme replaces the bar and the grid on filter / sort (AJAX): apply
     the current finish to whatever arrives */
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    /* a timer, not requestAnimationFrame: a tab in the background gets no
       animation frames, and the grid would then stay gold until the tab
       is looked at again */
    setTimeout(function () { scheduled = false; applyAll(true); }, 0);
  }

  function start() {
    /* every page: the cards follow the choice, with or without a switch */
    applyAll(true);
    /* after the theme's own start-up work on the product page */
    setTimeout(preselect, 0);
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('product-block').then(dashesFollow, function () { /* no such element: nothing to follow */ });
    }
    new MutationObserver(function (list) {
      for (var i = 0; i < list.length; i += 1) {
        var added = list[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches('product-block, ' + SWITCH) || n.querySelector('product-block, ' + SWITCH)) { schedule(); return; }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* QA 29 oct 2026: back/forward cache. A page the browser restores from
     memory (the back button after a product page) comes back exactly as it
     was left, this script included: a choice made on the product page in
     the meantime (silver there, the switch here still gold) was not
     shown until the next tap. Read the choice again and apply it. */
  window.addEventListener('pageshow', function (evt) {
    if (!evt.persisted) return;
    var now = 'gold';
    try { if (sessionStorage.getItem(KEY) === 'silver') now = 'silver'; } catch (e) { /* private mode: gold */ }
    if (now === state) return;
    state = now;
    applyAll(true);
  });
})();
